import React from "react";
import Layout from "../../components/Layout";
import OpenGraphTitle from "../../components/OpenGraphTitle";
import OpenGraphDescription from "../../components/OpenGraphDescription";
import Head from "../../components/head";
import { ServicesPageProvider } from "../../context/ServicesPageContext";
import { FranchiseeProvider } from "../../context/Franchisee";
import ServiceTitle from "../../components/ServiceTitle";
import ServicesDescription from "../../components/ServicesDescription";
import { Col, Row } from "react-bootstrap";
import Link from "next/link";
import ContactUs from "../../components/ContactUs";

interface MoveInMoveOutPageProps {
  /* add services page props here */
}

const MoveInMoveOut: React.FC<MoveInMoveOutPageProps> = () => (
  <FranchiseeProvider>
    <ServicesPageProvider>
      <Layout alternateFooter={true}>
        <Head
          slug="move-in-move-out"
          title="Move in & Move Out Cleaning Services - Two Maids"
          description="Moving is stressful. With Two Maids' Move In & Move Out Cleaning service, we eliminate that stress to make the moving process seamless and simple."
        />
        <OpenGraphTitle title="Move in & Move Out Cleaning Services - Two Maids" />

        <OpenGraphDescription>
          Moving is stressful. With Two Maids' Move In & Move Out Cleaning service, we eliminate that stress to make the moving process seamless and simple.
        </OpenGraphDescription>

        <ServiceTitle imageSrc="https://res.cloudinary.com/twomaidsengi/image/upload/v1724955998/hubsite-reskin/New%20Services%20Pages/Moving-In-Move-Out.webp">Move-in/Move-out</ServiceTitle>
        <Row className="service-description gray-background">
          <Col md={12} lg={7}>
            <h2>Moving Cleaning Services</h2>
            <p>
              Transitioning to a new home can be an exciting and sometimes overwhelming task. At Two Maids, we aim to make
              this transition as smooth and stress-free as possible with our Move In and Move Out Cleaning Services that
              are designed for the homeowner who needs to leave behind a clean home for sale.
            </p>
            <p>
              Perfect for cleaning needs before moving in or after moving out, this service makes sure your space is
              fresh for the first moment you step through the door, or for when you hand the keys off to someone else.
            </p>
          </Col>
          <Col md={12} lg={4} className="mx-auto">
            <h3 className="service-description-title gotham-bold">Comprehensive Home Cleaning for Every Room</h3>
            <p className="service-description-title">
              <i>
                Our service area list is as follows, with our professional cleaner team making sure the thorough
                cleaning checklist is taken care of by moving day.
              </i>
            </p>
            <Link href="#kitchen-cleaning">
              <a className="small-service-button mb-3">Kitchen Cleaning</a>
            </Link>
            <Link href="#bathroom-cleaning">
              <a className="small-service-button mb-3">Bathroom Cleaning</a>
            </Link>
            <Link href="#bedroom-cleaning">
              <a className="small-service-button mb-3">Bedroom Cleaning</a>
            </Link>
            <Link href="#living-areas">
              <a className="small-service-button mb-3">Living Areas Cleaning</a>
            </Link>
          </Col>
        </Row>
        <ServicesDescription id="kitchen-cleaning" colSize={12}>
          <h3 className="service-description-title">Kitchen Cleaning</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Deep clean inside/outside of microwave, oven, and refrigerator
                </li>
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Dust and clean windows, windowsills, and blinds
                </li>
                <li>
                  Tackle backsplash areas
                </li>
                <li>
                  Sweep and mop floors
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean the exterior of the dishwasher
                </li>
                <li>
                  Address stovetop, baseboards, countertops, sink, door and doorframes
                </li>
                <li>
                  Thoroughly clean inside/outside of cabinets and drawers
                </li>
                <li>
                  Sanitize pantry shelves, floor, and door
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription id="bathroom-cleaning" colSize={12} className="gray-background">
          <h3 className="service-description-title">Bathroom Cleaning</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Scrub shower and bathtubs, polish faucets
                </li>
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Sweep and mop floors
                </li>
                <li>
                  Sanitize toilets
                </li>
                <li>
                  Clean baseboards, counters, doors and doorframes, sink
                </li>
                <li>
                  Dust high areas like ceiling fans, light fixtures, and air vents
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Remove cobwebs
                </li>
                <li>
                  Clean inside/outside of cabinets and drawers
                </li>
                <li>
                  Address windows, windowsills, and blinds
                </li>
                <li>
                  Clean inside and outside of closets
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription id="bedroom-cleaning" colSize={12}>
          <h3 className="service-description-title">Bedroom Cleaning</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Sweep and mop floors; vacuum carpets
                </li>
                <li>
                  Dust high areas, including ceiling fans, light fixtures, and air vents
                </li>
                <li>
                  Remove cobwebs
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean mirrors and glass surfaces, shelves, and baseboards
                </li>
                <li>
                  Address doors and doorframes
                </li>
                <li>
                  Clean inside and outside of closets
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription id="living-areas" colSize={12} className="gray-background">
          <h3 className="service-description-title">Living Area Cleaning</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Sweep and mop floors; vacuum carpets and rugs
                </li>
                <li>
                  Clean counters, shelves
                </li>
                <li>
                  Dust high areas, including ceiling fans, light fixtures, and air vents
                </li>
                <li>
                  Remove cobwebs
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Thoroughly clean inside/outside of cabinets and drawers
                </li>
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Address doors and doorframes, baseboards
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <div className="service-description promise-description">
          <img className="service-description-section-promise-svg mr-3" src="https://res.cloudinary.com/twomaidsengi/image/upload/v1724955994/hubsite-reskin/New%20Services%20Pages/promise.svg" />
          <div>
            <h3 className="service-description-title">Our Promise: <i>The Maid Service That Ensures A Fresh Start</i></h3>
            <p>
              A clean slate is everything, both at the start and the end of your story. Whether you're moving into your dream
              home or closing the chapter on an old one, our Move In & Move Out Cleaning Package is designed to make sure that
              your space is thoroughly cleaned, refreshed, and inviting. Our experienced house cleaner team is dedicated to
              delivering an exceptional professional cleaning service, focusing on the details so you can focus on settling
              into your new life.
            </p>
          </div>
        </div>
        <ContactUs
          title="Ready for a Spotless Transition?"
          className="contact-us-flex"
          buttons={
            <Row className="d-flex justify-content-start">
              <Col md={"auto"} className="mb-3">
                <Link href={process.env.SALES_FUNNEL_URL}>
                  <a className="contact-us-button">BOOK YOUR CLEANING</a>
                </Link>
              </Col>
              <Col md={"auto"}>
                <Link href={process.env.SALES_FUNNEL_URL}>
                  <a className="contact-us-button">CALCULATE YOUR PRICE</a>
                </Link>
              </Col>
            </Row>
          }
        >
          <p>
            We want to make your move as seamless as possible. With our Move in & Move Out
            Cleaning Services, your old home will be transformed into a welcoming
            space ready for its next occupants. Or, the new home of your dreams will be
            move-in ready for you! With Two Maids as your partner in this transition, your
            move will be stress-free and full of promise for the future. Contact us today
            to schedule your Move In/Out Home Cleaning and start your bright new chapter.
          </p>
        </ContactUs>
      </Layout>
    </ServicesPageProvider>
  </FranchiseeProvider>
);
export default MoveInMoveOut;